import { useCallback, useContext } from 'react';
import axios from 'axios';
import SessionContext from '../../contexts/SessionContext';
import API_URL from '../../constants/environment';

export type DomainDrilldownDomain = 'location' | 'vendor' | 'insurance' | 'oem' | 'partType';

export interface DomainDrilldownOrderIdsInput {
    startDate: string;
    endDate: string;
    domain: DomainDrilldownDomain;
    id: string;
    locationId?: string;
    excludeEvs?: boolean;
}

interface DomainDrilldownOrderIdsResponse {
    orderIds: string[];
}

/**
 * Resolves the exact order IDs behind a domain stat on demand. Rollup documents no longer carry
 * per-order IDs, so drill-downs fetch them from the backend (which reproduces the reporting-stats
 * membership rules) only when a user clicks. Returns undefined on failure so callers can fall back
 * to the domain name filters alone.
 */
export const useDomainDrilldownOrderIds = () => {
    const sessionContext = useContext(SessionContext);
    const sessionId = sessionContext?.sessionId;

    return useCallback(async (input: DomainDrilldownOrderIdsInput): Promise<string[] | undefined> => {
        if (!sessionId || !input.id) return undefined;
        try {
            const { data } = await axios.post<DomainDrilldownOrderIdsResponse>(
                `${API_URL}/reporting/domain/drilldown-order-ids`,
                input,
                { headers: { Authorization: sessionId } },
            );
            return data.orderIds;
        } catch {
            return undefined;
        }
    }, [sessionId]);
};
